import { calendarDateToLocal, formatCalendarDate } from '../utils/calendarDate';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, CheckCircle, Wrench, Play, Zap,
  Plus, Trash2, X, CircleDot, Circle, Upload, AlertTriangle,
  ShieldCheck,
} from 'lucide-react';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import { TENANT_PATHS } from '../routing/paths.ts';

const AP_STATUS_CONFIG = {
  not_started: { label: 'Not Started', icon: Circle,      color: 'text-gray-500',  bg: 'bg-gray-100'  },
  in_progress:  { label: 'In Progress', icon: CircleDot,   color: 'text-blue-600',  bg: 'bg-blue-50'   },
  completed:    { label: 'Completed',   icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50'  },
};

const REMEDIATION_ACTIVE_STATUSES = new Set([
  'plan_accepted', 'remediation_in_progress', 'remediation_complete',
  'pending_verification', 'verification_complete', 'verification_failed',
]);

export default function RemediationExecution() {
  const navigate   = useNavigate();
  const { id }     = useParams();

  const [issue,          setIssue]          = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [actionPlans,    setActionPlans]    = useState([]);
  const [apLoading,      setApLoading]      = useState(false);
  const [advancing,      setAdvancing]      = useState(false);
  const [errorMsg,       setErrorMsg]       = useState('');

  // Completion modal
  const [completionModal, setCompletionModal] = useState(null); // { apId, apTitle }
  const [completionNotes, setCompletionNotes] = useState('');
  const [completionSubmitting, setCompletionSubmitting] = useState(false);

  // Evidence upload
  const evidenceFileRef = useRef(null);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [apEvidenceRef, setApEvidenceRef] = useState(null);

  // ── Load issue + action plans ──────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [issRes, apRes] = await Promise.all([
          axiosInstance.get(`/api/sox-issues/${id}`),
          axiosInstance.get(`/api/sox-issues/${id}/action-plans`),
        ]);
        const issData = issRes.data;
        if (!REMEDIATION_ACTIVE_STATUSES.has(issData.status)) {
          navigate(TENANT_PATHS.ISSUE_DETAIL.replace(':id', id));
          return;
        }
        setIssue(issData);
        setActionPlans(Array.isArray(apRes.data) ? apRes.data : apRes.data?.action_plans || []);
      } catch (e) {
        console.error(e);
        setErrorMsg('Failed to load issue.');
      } finally { setLoading(false); }
    };
    load();
  }, [id, navigate]);

  // ── Action plan status change ──────────────────────────────────────────────
  const handleApStatusChange = async (apId, newStatus, completionNotesText) => {
    try {
      const body = { status: newStatus };
      if (completionNotesText) body.completion_notes = completionNotesText;
      await axiosInstance.put(`/api/sox-issues/${id}/action-plans/${apId}/status`, body);
      setActionPlans(prev => prev.map(p =>
        p.id === apId
          ? { ...p, status: newStatus, completion_notes: completionNotesText || p.completion_notes, completed_at: newStatus === 'completed' ? new Date().toISOString() : p.completed_at }
          : p
      ));
    } catch (e) { console.error(e); }
  };

  const openCompletionModal = (ap) => {
    setCompletionModal({ apId: ap.id, apTitle: ap.title });
    setCompletionNotes('');
  };

  const submitCompletion = async () => {
    if (!completionNotes.trim()) return;
    setCompletionSubmitting(true);
    await handleApStatusChange(completionModal.apId, 'completed', completionNotes.trim());
    setCompletionSubmitting(false);
    setCompletionModal(null);
  };

  // ── Evidence upload ────────────────────────────────────────────────────────
  const handleEvidenceUpload = async (file, actionPlanId) => {
    if (!file) return;
    setUploadingEvidence(true);
    try {
      const body = {
        file_ref:       file.name,
        filename:       file.name,
        file_type:      file.type || undefined,
        file_size:      file.size || undefined,
        action_plan_id: actionPlanId || undefined,
      };
      await axiosInstance.post(`/api/sox-issues/${id}/evidence`, body);
    } catch (e) { console.error(e); }
    finally { setUploadingEvidence(false); setApEvidenceRef(null); }
  };

  // ── Mark Remediation Complete ──────────────────────────────────────────────
  const handleMarkComplete = async () => {
    setAdvancing(true);
    try {
      await axiosInstance.put(`/api/sox-issues/${id}/status`, { status: 'remediation_complete' });
      setIssue(prev => ({ ...prev, status: 'remediation_complete' }));
      navigate(TENANT_PATHS.ISSUE_DETAIL.replace(':id', id));
    } catch (e) {
      const msg = resolve403ErrorMessage(e, e.response?.data?.error || 'Failed to update status');
      setErrorMsg(msg);
      setTimeout(() => setErrorMsg(''), 4000);
    } finally { setAdvancing(false); }
  };

  // ── Auto-advance to remediation_in_progress if still plan_accepted ─────────
  useEffect(() => {
    if (!issue) return;
    if (issue.status === 'plan_accepted') {
      axiosInstance.put(`/api/sox-issues/${id}/status`, { status: 'remediation_in_progress' })
        .then(() => setIssue(prev => ({ ...prev, status: 'remediation_in_progress' })))
        .catch(console.error);
    }
  }, [issue?.status, id]);

  // ── Computed ───────────────────────────────────────────────────────────────
  const totalAps    = actionPlans.length;
  const doneAps     = actionPlans.filter(p => p.status === 'completed').length;
  const progressPct = totalAps > 0 ? Math.round((doneAps / totalAps) * 100) : 0;
  const allDone     = totalAps > 0 && doneAps === totalAps;
  const canMarkComplete = allDone && issue?.status === 'remediation_in_progress';

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
    </div>
  );

  if (!issue) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <p className="text-gray-500">{errorMsg || 'Issue not found.'}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hidden file input for evidence */}
      <input
        ref={evidenceFileRef}
        type="file"
        className="hidden"
        onChange={e => {
          handleEvidenceUpload(e.target.files?.[0], apEvidenceRef);
          e.target.value = '';
        }}
      />

      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(TENANT_PATHS.ISSUE_DETAIL.replace(':id', id))}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />Back to Issue
            </button>
            <div className="h-4 w-px bg-gray-200" />
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs font-mono text-gray-400 shrink-0">{issue.issue_id}</span>
              <h1 className="text-sm font-semibold text-gray-900 truncate">{issue.title || 'Untitled Issue'}</h1>
            </div>
            <div className="ml-auto flex items-center gap-2 shrink-0">
              {(issue.is_escalated || issue.status === 'escalated') && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                  <Zap className="h-3 w-3" />Escalated
                </span>
              )}
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-200">
                <Wrench className="h-3 w-3" />
                {issue.status === 'remediation_in_progress' ? 'In Progress'
                  : issue.status === 'remediation_complete' ? 'Complete'
                  : issue.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="max-w-4xl mx-auto px-6 py-6 space-y-6">

        {errorMsg && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />{errorMsg}
          </div>
        )}

        {/* Progress */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-gray-900">Remediation Progress</h2>
            <span className="text-sm font-semibold text-gray-700">{doneAps} / {totalAps} complete</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2.5">
            <div
              className="bg-green-500 h-2.5 rounded-full transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {allDone && (
            <p className="mt-2 text-xs text-green-600 font-medium flex items-center gap-1">
              <CheckCircle className="h-3.5 w-3.5" />All action plans complete
            </p>
          )}
        </div>

        {/* Action plans */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
            <Wrench className="h-4 w-4 text-blue-600" />
            <h2 className="text-base font-bold text-gray-900">Action Plans</h2>
          </div>

          {apLoading ? (
            <div className="px-6 py-8 text-center"><Loader2 className="h-5 w-5 text-gray-400 animate-spin mx-auto" /></div>
          ) : actionPlans.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-gray-400">No action plans yet. Add them in the Issue Remediation tab.</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {actionPlans.map(ap => {
                const parsed = (() => {
                  try { return typeof ap.description === 'string' ? JSON.parse(ap.description) : ap.description || {}; }
                  catch { return {}; }
                })();
                const isOverdue = ap.due_date && ap.status !== 'completed' && calendarDateToLocal(ap.due_date) < new Date();
                const cfg = AP_STATUS_CONFIG[ap.status] || AP_STATUS_CONFIG.not_started;

                return (
                  <div key={ap.id} className="px-6 py-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                            {React.createElement(cfg.icon, { className: 'h-3 w-3' })}
                            {cfg.label}
                          </span>
                          {isOverdue && (
                            <span className="text-xs font-medium text-red-500 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">Overdue</span>
                          )}
                        </div>
                        <p className="text-sm font-semibold text-gray-900 mb-1">{ap.title}</p>
                        {parsed.root_cause && (
                          <p className="text-xs text-gray-500 mb-0.5"><span className="font-medium">Root Cause:</span> {parsed.root_cause}</p>
                        )}
                        {parsed.corrective_action && (
                          <p className="text-xs text-gray-500"><span className="font-medium">Corrective Action:</span> {parsed.corrective_action}</p>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                          {ap.owner_name && <span>{ap.owner_name}{ap.owner_role ? ` · ${ap.owner_role}` : ''}</span>}
                          {ap.due_date && (
                            <span className={isOverdue ? 'text-red-500 font-medium' : ''}>
                              Due {formatCalendarDate(ap.due_date, 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          )}
                          {ap.ticket_reference && <span>{ap.ticket_reference}</span>}
                        </div>

                        {ap.status === 'completed' && ap.completion_notes && (
                          <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mt-3">
                            <CheckCircle className="h-3.5 w-3.5 text-green-600 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-xs text-green-700 font-semibold mb-0.5">
                                Completed {ap.completed_at ? new Date(ap.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                              </p>
                              <p className="text-xs text-green-800">{ap.completion_notes}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center gap-2 shrink-0">
                        {ap.status === 'not_started' && (
                          <button
                            onClick={() => handleApStatusChange(ap.id, 'in_progress')}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                          >
                            <Play className="h-3 w-3" />Start
                          </button>
                        )}
                        {ap.status === 'in_progress' && (
                          <button
                            onClick={() => openCompletionModal(ap)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                          >
                            <CheckCircle className="h-3 w-3" />Mark Complete
                          </button>
                        )}
                        {ap.status !== 'completed' && (
                          <button
                            onClick={() => { setApEvidenceRef(ap.id); evidenceFileRef.current?.click(); }}
                            disabled={uploadingEvidence}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                          >
                            {uploadingEvidence && apEvidenceRef === ap.id
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : <Upload className="h-3 w-3" />}
                            Add Files
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Mark Remediation Complete CTA */}
        {issue.status === 'remediation_in_progress' && (
          <div className={`rounded-xl border px-6 py-4 flex items-center justify-between ${canMarkComplete ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center gap-2">
              {canMarkComplete
                ? <CheckCircle className="h-5 w-5 text-green-600" />
                : <Wrench className="h-5 w-5 text-gray-400" />}
              <div>
                <p className={`text-sm font-semibold ${canMarkComplete ? 'text-green-800' : 'text-gray-500'}`}>
                  {canMarkComplete ? 'All action plans complete' : `${totalAps - doneAps} action plan(s) remaining`}
                </p>
                <p className={`text-xs ${canMarkComplete ? 'text-green-600' : 'text-gray-400'}`}>
                  {canMarkComplete ? 'Mark remediation as complete to proceed to verification.' : 'Complete all action plans to mark remediation done.'}
                </p>
              </div>
            </div>
            <button
              onClick={handleMarkComplete}
              disabled={!canMarkComplete || advancing}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm shrink-0"
            >
              {advancing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              {advancing ? 'Saving…' : 'Mark Remediation Complete'}
            </button>
          </div>
        )}

        {/* Remediation already complete info */}
        {['remediation_complete','pending_verification','verification_complete','verification_failed'].includes(issue.status) && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-6 py-4 flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-blue-600 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-blue-800">Remediation marked complete</p>
              <p className="text-xs text-blue-600">This issue is now in the verification phase. Return to the issue to manage verification.</p>
            </div>
            <button
              onClick={() => navigate(TENANT_PATHS.ISSUE_DETAIL.replace(':id', id))}
              className="ml-auto flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors shrink-0"
            >
              <ArrowLeft className="h-3.5 w-3.5" />Back to Issue
            </button>
          </div>
        )}

      </div>

      {/* ── Completion Modal ── */}
      {completionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-base font-bold text-gray-900">Mark Complete</h3>
                <p className="text-sm text-gray-500 mt-0.5 truncate max-w-xs">{completionModal.apTitle}</p>
              </div>
              <button onClick={() => setCompletionModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs text-gray-500 font-medium mb-1.5">Completion Notes <span className="text-red-500">*</span></label>
                <textarea
                  rows={4}
                  value={completionNotes}
                  onChange={e => setCompletionNotes(e.target.value)}
                  placeholder="Describe what was done to complete this action plan…"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                  autoFocus
                />
              </div>
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
                <p className="text-xs text-amber-700">
                  Consider uploading supporting evidence via <strong>Add Files</strong> on the action plan before completing.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 pb-6">
              <button onClick={() => setCompletionModal(null)} className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={submitCompletion}
                disabled={!completionNotes.trim() || completionSubmitting}
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 disabled:opacity-40 transition-colors"
              >
                {completionSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                {completionSubmitting ? 'Saving…' : 'Mark Done'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
